import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import VideoGallery from '@/components/ui/video-gallery';
import { Play, Sparkles, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SmartTag } from '@/components/ui/tag';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  youtubeId: string;
  category: string;
  tags: string[];
  url_slug?: string;
}

interface Category {
  name: string;
  tags: string[];
  description: string;
}

interface SettingsResponse {
  categories: Category[];
}

interface TabbedVideoGalleryProps {
  workflowVideos: VideoItem[];
  coolFeatureVideos: VideoItem[];
  settings?: SettingsResponse | null;
  isLoading?: boolean;
  className?: string;
  slugParam?: string | null;
  navigate?: (path: string, options?: { replace?: boolean }) => void;
}

const TabbedVideoGallery = ({ 
  workflowVideos, 
  coolFeatureVideos, 
  settings,
  isLoading = false,
  className = "",
  slugParam,
  navigate
}: TabbedVideoGalleryProps) => {
  const [activeTab, setActiveTab] = useState("workflow");
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  // Handle slug parameter to open specific video
  useEffect(() => {
    if (slugParam) {
      const allVideos = [...workflowVideos, ...coolFeatureVideos];
      const videoBySlug = allVideos.find(video => {
        return video.url_slug === slugParam;
      });
      
      if (videoBySlug) {
        setSelectedVideo(videoBySlug);
        // Determine which tab the video belongs to
        if (workflowVideos.some(v => v.id === videoBySlug.id)) {
          setActiveTab("workflow");
        } else {
          setActiveTab("features");
        }
      }
    }
  }, [slugParam, workflowVideos, coolFeatureVideos]);

  const getActiveVideos = () => {
    return activeTab === "workflow" ? workflowVideos : coolFeatureVideos;
  };

  const filteredVideos = getActiveVideos().filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(selectedTag => video.tags.includes(selectedTag));
    
    return matchesSearch && matchesTags;
  });



  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleVideoClick = (video: VideoItem) => {
    setSelectedVideo(video);
    if (navigate) {
      const newUrl = `/getting-started?slug=${video.url_slug || `video-${video.id}`}`;
      navigate(newUrl, { replace: true });
    }
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
    if (navigate) {
      navigate('/getting-started', { replace: true });
    }
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  // Reset filters when switching tabs
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSelectedTags([]);
  };

  // Get category descriptions from settings
  const getCategoryDescription = (categoryName: string) => {
    if (!settings || !settings.categories) {
      return '';
    }
    
    const category = settings.categories.find(cat => cat.name === categoryName);
    return category?.description || '';
  };

  // Get category names from settings
  const getCategoryNames = () => {
    if (!settings || !settings.categories) {
      return ['Workflow Essentials', 'Cool Features'];
    }
    
    return settings.categories.map(cat => cat.name);
  };

  // Get tags for a specific category from settings
  const getCategoryTags = (categoryName: string) => {
    if (!settings || !settings.categories) {
      return [];
    }
    
    const category = settings.categories.find(cat => cat.name === categoryName);
    return category?.tags || [];
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex justify-center items-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading video gallery...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Search Input */}
      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex justify-center mb-8">
          <TabsList className={`grid grid-cols-${getCategoryNames().length} h-16 bg-gradient-to-r from-blue-50/50 to-teal-50/50 dark:from-blue-900/20 dark:to-teal-900/20 rounded-xl p-2 border border-border/50 w-full max-w-md`}>
            {getCategoryNames().map((categoryName, index) => (
              <TabsTrigger 
                key={categoryName}
                value={index === 0 ? "workflow" : "features"}
                className="text-sm sm:text-base font-semibold h-12 rounded-lg transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center gap-1 sm:gap-2 px-2 sm:px-3"
              >
                {index === 0 ? <Play className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" /> : <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />}
                <span className="truncate text-xs sm:text-sm md:text-base">{categoryName}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        
        <TabsContent value="workflow" className="space-y-6">
          <div className="text-center max-w-5xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed">
              {getCategoryDescription(getCategoryNames()[0])}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {getCategoryTags(getCategoryNames()[0]).map((tag) => (
              <SmartTag 
                key={tag} 
                className={`text-xs cursor-pointer hover:opacity-80 transition-all duration-200 hover:scale-105 ${
                  selectedTags.includes(tag) ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </SmartTag>
            ))}
          </div>
          {(selectedTags.length > 0 || searchQuery) && (
            <div className="flex justify-center mb-4">
              <button
                onClick={clearFilters}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
              >
                Clear all filters ({filteredVideos.length} of {workflowVideos.length} videos)
              </button>
            </div>
          )}
          <VideoGallery 
            videos={filteredVideos} 
            gridCols="default" 
            onTagClick={toggleTag} 
            selectedTags={selectedTags}
            onVideoClick={handleVideoClick}
            selectedVideo={selectedVideo}
            onCloseVideo={handleCloseVideo}
          />
        </TabsContent>
        
        <TabsContent value="features" className="space-y-6">
          <div className="text-center max-w-5xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed">
              {getCategoryDescription(getCategoryNames()[1])}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {getCategoryTags(getCategoryNames()[1]).map((tag) => (
              <SmartTag 
                key={tag} 
                className={`text-xs cursor-pointer hover:opacity-80 transition-all duration-200 hover:scale-105 ${
                  selectedTags.includes(tag) ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </SmartTag>
            ))}
          </div>
          {(selectedTags.length > 0 || searchQuery) && (
            <div className="flex justify-center mb-4">
              <button
                onClick={clearFilters}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
              >
                Clear all filters ({filteredVideos.length} of {coolFeatureVideos.length} videos)
              </button>
            </div>
          )}
          <VideoGallery 
            videos={filteredVideos} 
            gridCols="dense" 
            onTagClick={toggleTag} 
            selectedTags={selectedTags}
            onVideoClick={handleVideoClick}
            selectedVideo={selectedVideo}
            onCloseVideo={handleCloseVideo}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TabbedVideoGallery;